import { useState, useEffect, useRef } from 'react';

/**
 * Hook to manage course session timer
 * Automatically persists to localStorage and syncs with server
 */
export const useCourseTimer = (sessionId, onUpdate) => {
    const [timeInSeconds, setTimeInSeconds] = useState(0);
    const [isRunning, setIsRunning] = useState(false);
    const intervalRef = useRef(null);
    const lastSaveRef = useRef(0);

    const SAVE_INTERVAL = 30; // Save every 30 seconds

    // Start timer
    const start = (initialTime = 0) => {
        setTimeInSeconds(initialTime);
        setIsRunning(true);
    };

    // Pause timer
    const pause = () => {
        setIsRunning(false);
    };

    // Resume timer
    const resume = () => {
        setIsRunning(true);
    };

    // Reset timer
    const reset = () => {
        setTimeInSeconds(0);
        setIsRunning(false);
        lastSaveRef.current = 0;
    };

    // Stop and save
    const stop = () => {
        setIsRunning(false);
        if (onUpdate && sessionId) {
            onUpdate(sessionId, timeInSeconds);
        }
    };

    // Timer effect
    useEffect(() => {
        if (isRunning) {
            intervalRef.current = setInterval(() => {
                setTimeInSeconds(prev => {
                    const newTime = prev + 1;

                    // Auto-save at intervals
                    if (newTime - lastSaveRef.current >= SAVE_INTERVAL) {
                        lastSaveRef.current = newTime;
                        if (onUpdate && sessionId) {
                            onUpdate(sessionId, newTime);
                        }
                    }

                    return newTime;
                });
            }, 1000);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRunning, sessionId, onUpdate]);

    // Format time display
    const formatTime = () => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = timeInSeconds % 60;

        if (hours > 0) {
            return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return {
        timeInSeconds,
        formattedTime: formatTime(),
        isRunning,
        start,
        pause,
        resume,
        reset,
        stop
    };
};

export default useCourseTimer;
